import { Link } from 'react-router';
import type { ReactNode } from 'react';

/**
 * The privacy notice and terms, as required by COMPLIANCE.md §2.
 *
 * The wording is the compliance document's, close to verbatim: it was written
 * to be legally sufficient and honest at the same time, and paraphrasing it in
 * the name of tone would put those at risk. Two placeholders remain — the
 * operator's name and contact address — and they are marked in the text rather
 * than filled with something plausible.
 */

const OPERATOR = 'Dr Tom Sanderson';
const CONTACT = 'privacy@iculogbook.co.uk';
const UPDATED = '19 August 2026';

function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    /* Outside AppShell, so it clears the notch itself. See AuthLayout. */
    <main className="mx-auto min-h-dvh max-w-content bg-bg px-4 py-8 pt-[calc(2rem+env(safe-area-inset-top))] pb-16">
      <p className="text-xs font-strong tracking-widest text-faint uppercase">ICULogbook</p>
      {/* Page heading — the serif marks the top of the hierarchy, as in the
          app header and the entry list's date groups. */}
      <h1 className="pt-2 font-serif text-3xl font-strong tracking-tight text-text">{title}</h1>
      <p className="pt-1 text-xs text-faint">Last updated: {UPDATED}</p>

      <div className="flex flex-col gap-4 pt-6 text-sm leading-relaxed text-muted">{children}</div>

      <nav className="flex gap-4 pt-10 text-sm">
        <Link to="/privacy" className="text-accent underline hover:text-accent-hover">
          Privacy notice
        </Link>
        <Link to="/terms" className="text-accent underline hover:text-accent-hover">
          Terms
        </Link>
        <Link to="/entries" className="text-accent underline hover:text-accent-hover">
          Back to the app
        </Link>
      </nav>
    </main>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h2 className="pt-2 text-base font-strong text-text">{children}</h2>;
}

export function PrivacyRoute() {
  return (
    <LegalPage title="Privacy notice">
      <p>
        ICULogbook is a personal logbook for UK Intensive Care Medicine trainees. This notice
        explains what we hold about you and what you can do about it.
      </p>

      <Heading>Who we are</Heading>
      <p>
        ICULogbook is operated by {OPERATOR}, who is the data controller. Contact:{' '}
        <a href={`mailto:${CONTACT}`} className="text-accent underline hover:text-accent-hover">
          {CONTACT}
        </a>
        .
      </p>

      <Heading>What we collect</Heading>
      <ul className="flex list-disc flex-col gap-2 pl-5">
        <li>
          Your email address and a securely hashed version of your password, so you can sign in.
        </li>
        <li>Your display name, so it can appear on the PDFs you generate.</li>
        <li>
          The logbook entries you create: procedures and cases, with dates, times, an age band, a
          capability level and any notes you write.
        </li>
      </ul>

      <Heading>What we do not collect</Heading>
      <p>
        We do not collect any information about patients. There is nowhere in ICULogbook to record a
        patient’s name, NHS number, hospital number, date of birth or exact age. We do not collect
        your GMC number — if you choose to add it to a PDF, it is used to create that file and is
        not stored. We do not use advertising, or any third-party analytics or tracking service — no
        Google Analytics, no cookies that follow you between sites, no device fingerprinting.
      </p>

      <Heading>Understanding how the service is used</Heading>
      <p>
        So we can keep ICULogbook running and see whether it is useful, the operator can view totals
        across all accounts — how many people have signed up, how many have logged something
        recently, and which procedures are recorded most often. These are counts only. They are
        worked out from information already held (when an account was created, and when entries were
        saved); nothing extra is recorded about you, and no page views, clicks or browsing are
        monitored. This view never shows the content of anyone’s entries.
      </p>

      <Heading>Why we can do this</Heading>
      <p>
        We process your data to provide the service you have asked for. Our lawful basis under UK
        GDPR Article 6(1)(b) is performance of a contract with you. For the usage totals described
        above, our lawful basis is Article 6(1)(f), legitimate interests — our interest in keeping
        the service running and knowing whether it works. This involves counts only, so the impact
        on you is minimal, and you can object at any time using the contact address above.
      </p>

      <Heading>Where it is stored</Heading>
      <p>
        On servers in the United Kingdom or European Economic Area, provided by Railway. Password
        reset emails are sent using Resend. If you send us feedback from within the app, that
        message is emailed to us through Resend and is not stored in our database.
      </p>

      <Heading>How long we keep it</Heading>
      <p>
        For as long as your account is open. If you delete your account, your data is scheduled for
        permanent deletion after 30 days — a grace period in case of accidental or malicious
        deletion. After that it is irretrievably removed, including from backups within a further 30
        days.
      </p>

      <Heading>Your rights</Heading>
      <p>
        You can access, correct, export and delete your data at any time from within the app —
        Settings contains a one-tap export of everything we hold about you, and a delete-my-account
        option. You also have the right to complain to the Information Commissioner’s Office at{' '}
        <a
          href="https://ico.org.uk"
          className="text-accent underline hover:text-accent-hover"
          rel="noreferrer"
        >
          ico.org.uk
        </a>
        .
      </p>

      <Heading>Your responsibility</Heading>
      <p>
        Please do not enter patient-identifiable information into the free-text fields. ICULogbook
        is designed so that you do not need to, and your professional obligations under GMC guidance
        apply to anything you write.
      </p>

      <Heading>Security</Heading>
      <p>
        Passwords are hashed with argon2id. All traffic is encrypted in transit. Access to the
        production database is restricted to the operator.
      </p>

      <Heading>Changes</Heading>
      <p>We will post any changes here and, for significant changes, email you.</p>
    </LegalPage>
  );
}

export function TermsRoute() {
  return (
    <LegalPage title="Terms of use">
      <Heading>The service</Heading>
      <p>
        ICULogbook is provided free of charge and as-is. We make no guarantee that it will be
        available without interruption, and we may change or withdraw it. If it is ever withdrawn,
        you will be given notice and your export will continue to work — the format is documented
        publicly so that your record does not depend on us existing.
      </p>

      <Heading>Your record is yours, and so is its accuracy</Heading>
      <p>
        Everything in your logbook is self-reported by you. You are responsible for the accuracy of
        what you record, exactly as you would be for a paper logbook or any other portfolio
        evidence.
      </p>

      <Heading>Keep your own backups</Heading>
      <p>
        Please export your logbook regularly from Settings and keep the file somewhere you control.
        We take backups and we take them seriously, but the only copy nobody else can lose for you
        is the one you hold.
      </p>

      <Heading>No affiliation</Heading>
      <p>
        ICULogbook is not endorsed by, affiliated with, or approved by the Faculty of Intensive Care
        Medicine, the Royal College of Anaesthetists, the General Medical Council, or any deanery or
        training programme.
      </p>

      <Heading>No guarantee of acceptance</Heading>
      <p>
        We make no warranty that a summary generated by ICULogbook will be accepted by any ARCP
        panel, Educational Supervisor or Training Programme Director. What evidence is acceptable is
        a matter for them.
      </p>

      <Heading>No patient data</Heading>
      <p>
        You must not enter patient-identifiable information into ICULogbook. The app is designed so
        that you never need to.
      </p>
    </LegalPage>
  );
}
